import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../core/services/theme.service';
import { TitleService } from '../core/services/title.service';
import { createDashboardDateAxis, DashboardTimeWindow } from './dashboard-date-axis';

type DashboardGeography = 'organization' | 'county' | 'state' | 'us';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DashboardComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('chart') chartElement!: ElementRef<HTMLDivElement>;
  readonly geographyOptions: { value: DashboardGeography; labelKey: string }[] = [
    { value: 'organization', labelKey: 'Dashboard.GeographyOrganization' },
    { value: 'county', labelKey: 'Dashboard.GeographyCounty' },
    { value: 'state', labelKey: 'Dashboard.GeographyState' },
    { value: 'us', labelKey: 'Dashboard.GeographyUs' }
  ];
  readonly timeWindowOptions: { value: DashboardTimeWindow; labelKey: string }[] = [
    { value: 7, labelKey: 'Dashboard.TimeWindow7Days' },
    { value: 14, labelKey: 'Dashboard.TimeWindow14Days' },
    { value: 30, labelKey: 'Dashboard.TimeWindow1Month' }
  ];
  selectedGeography: DashboardGeography = 'organization';
  selectedTimeWindow: DashboardTimeWindow = 7;
  private plotly?: typeof import('plotly.js-dist-min').default;
  private chartInitialized = false;
  private isDestroyed = false;
  private languageSubscription?: Subscription;

  constructor(
    private titleService: TitleService,
    private themeService: ThemeService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.themeService.enableSummaryTheme();
    this.titleService.updateTitleTranslateKey('Dashboard.Title');
    this.languageSubscription = this.translate.onLangChange.subscribe(() => {
      void this.updateChart();
    });
  }

  ngAfterViewInit(): void {
    void this.renderChart();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.languageSubscription?.unsubscribe();
    if (this.plotly && this.chartInitialized) {
      this.plotly.purge(this.chartElement.nativeElement);
    }
  }

  selectGeography(geography: DashboardGeography): void {
    this.selectedGeography = geography;
  }

  selectTimeWindow(days: DashboardTimeWindow): void {
    this.selectedTimeWindow = days;
    void this.updateChart();
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    void this.updateChart();
  }

  private async renderChart(): Promise<void> {
    try {
      const { default: plotly } = await import('plotly.js-dist-min');
      if (this.isDestroyed) {
        return;
      }

      this.plotly = plotly;
      await plotly.newPlot(this.chartElement.nativeElement, {
        data: [],
        layout: this.getChartLayout(),
        config: { responsive: true, displayModeBar: false }
      });
      this.chartInitialized = true;
      if (this.isDestroyed) {
        plotly.purge(this.chartElement.nativeElement);
      }  else {
        await this.updateChart();
      }
    } catch (error: unknown) {
      console.error('Unable to render the Dashboard Plotly chart.', error);
    }
  }

  private async updateChart(): Promise<void> {
    if (!this.plotly || !this.chartInitialized || this.isDestroyed) {
      return;
    }

    try {
      await this.plotly.relayout(this.chartElement.nativeElement, this.getChartLayout());
    } catch (error: unknown) {
      console.error('Unable to update the Dashboard Plotly chart.', error);
    }
  }

  private getChartLayout(): Partial<import('plotly.js-dist-min').Layout> {
    const chartWidth = this.chartElement.nativeElement.clientWidth || window.innerWidth;
    const dateAxis = createDashboardDateAxis(
      this.selectedTimeWindow,
      new Date(),
      this.translate.getCurrentLang() || 'en',
      chartWidth
    );

    return {
      autosize: true,
      showlegend: false,
      margin: { l: 60, r: 20, t: 60, b: 50 },
      title: {
        text: '<span style="font-weight:500">' + String(this.translate.instant('Dashboard.ChartTitle')) + '</span>',
        font: { weight: 'bold' }
      },
      xaxis: {
        type: 'date',
        range: [dateAxis.range[0], dateAxis.range[1]],
        tickmode: 'array',
        tickvals: dateAxis.tickValues,
        ticktext: dateAxis.tickLabels,
        tickangle: 90,
        tickfont: { weight: 'bold' },
        ticklabelstandoff: 15,
        ticks: 'outside',
        ticklen: 10,
        showgrid: false,
        zeroline: true,
        automargin: true,
        showline: true,
        linecolor: '#666666',
        linewidth: 2,
      },
      yaxis: {
        title: {
          text: '<span style="font-weight:500">' + String(this.translate.instant('Dashboard.ChartYAxis')) + '</span>',
          font: { weight: 'bold' }
        },
        range: [0, 0.1],
        tickformat: '.0%',
        tickfont: { weight: 'bold' },
        ticklabelstandoff: 10,
        dtick: 0.02,
        showgrid: false,
        showline: true,
        linecolor: '#666666',
        linewidth: 2,
        zeroline: true,
        automargin: true
      }
    };
  }
}
